// src/components/FullTeam.jsx
// Fixed: uses member.photo (not member.image), overlay content restored,
// 3+2 row layout, LinkedIn URLs from teamData.js

import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import teamMembers from "./data/TeamData";
import "./FullTeam.css";

//import FAQ from "./FAQ";
import { teamFaqSchema } from "../schemas/teamSchema";

const LinkedInIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const ArrowIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    stroke="currentColor"
  >
    <path d="M7 17L17 7M17 7H7M17 7v10" />
  </svg>
);

const MemberCard = ({ member, index }) => {
  const handleImageError = (e) => {
    e.target.style.display = "none";
    const ph = e.target.parentNode.querySelector(".ft-photo-placeholder");
    if (ph) ph.style.display = "flex";
  };

  const handleLinkedIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(member.linkedin, "_blank", "noopener,noreferrer");
  };

  return (
    <Link
      to={`/team/${member.id}`}
      className="ft-card"
      aria-label={`View ${member.name}'s profile`}
    >
      {/* ── Photo ── */}
      <div className="ft-photo-wrap">
        <span className="ft-index">{String(index + 1).padStart(2, "0")}</span>

        {/* member.photo is the correct key from teamData.js */}
        <img
          src={member.photo}
          alt={member.name}
          className="ft-photo"
          loading={index < 3 ? "eager" : "lazy"}
          onError={handleImageError}
        />

        {/* Initials fallback shown if image 404s */}
        <div className="ft-photo-placeholder">
          <span>
            {member.name
              .split(" ")
              .map((n) => n[0])
              .join("")}
          </span>
        </div>

        {/* Hover overlay */}
        <div className="ft-overlay">
          <div className="ft-overlay-bottom">
            <div className="ft-overlay-info">
              <p className="ft-overlay-name"></p>
              <p className="ft-overlay-role"></p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="ft-card-body">
        <div className="ft-card-text">
          <h3 className="ft-card-name">{member.name}</h3>
          <p className="ft-card-role">{member.title}</p>
        </div>
        <div className="ft-card-arrow">
          <ArrowIcon />
        </div>
      </div>
    </Link>
  );
};

const FullTeam = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const row1 = teamMembers.slice(0, 3);
  const row2 = teamMembers.slice(3, 5);

  return (
    <div className="ft-page">
      {/* ── Hero ── */}
      <section className="ft-hero">
        <div className="ft-hero-inner">
          <div>
            <p className="ft-hero-eyebrow">Our People</p>
            <h1 className="ft-hero-title">
              Meet Our <em>Expert</em>
              <br />
              CFO Team
            </h1>
          </div>
          <p className="ft-hero-sub">
            Strategic Fractional CFO leadership with proven expertise across
            industries — deployed precisely when your business needs it most.
          </p>
        </div>
      </section>

      {/* ── Grid: 3 + 2 ── */}
      <section className="ft-grid-section">
        {/* Row 1 — 3 equal cards */}
        <div className="ft-row ft-row-3">
          {row1.map((member, i) => (
            <MemberCard key={member.id} member={member} index={i} />
          ))}
        </div>

        {/* Row 2 — 2 equal cards, centered */}
        <div className="ft-row ft-row-2">
          {row2.map((member, i) => (
            <MemberCard key={member.id} member={member} index={i + 3} />
          ))}
        </div>
      </section>

      {/* ── FAQ Section ── 
      <FAQ
        faqs={teamFaqSchema.mainEntity.map((q) => ({
          heading: q.name,
          description: q.acceptedAnswer.text,
        }))}
      />*/}

      {/* ── CTA Strip ── */}
      <div className="svc-cta-strip">
        <div className="svc-cta-strip__inner">
          <p className="svc-cta-strip__text">
            Not sure which service fits your needs?
          </p>
          <a
            href="/#contact"
            className="svc-cta-strip__btn"
            onClick={(e) => {
              e.preventDefault();
              if (location.pathname !== "/") {
                navigate("/");
                setTimeout(() => {
                  const el = document.getElementById("contact");
                  if (el)
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 300);
                return;
              }
              const el = document.getElementById("contact");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            Schedule a Free Consultation
            <svg
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 10h12M10 4l6 6-6 6" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
};

export default FullTeam;
